import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface ChartDataPoint {
  date: string;
  views: number;
  starts: number;
  responses: number;
  completionRate: number;
}

interface PerformanceChartProps {
  data: ChartDataPoint[];
}

const METRICS = [
  { key: 'views', label: 'Views', color: 'hsl(217, 91%, 60%)' },
  { key: 'starts', label: 'Starts', color: 'hsl(134, 61%, 48%)' },
  { key: 'responses', label: 'Responses', color: 'hsl(280, 70%, 55%)' },
  { key: 'completionRate', label: 'Completion Rate', color: 'hsl(35, 90%, 55%)' },
] as const;

export function PerformanceChart({ data }: PerformanceChartProps) {
  const [activeMetric, setActiveMetric] = useState<string>('views');
  const metric = METRICS.find((m) => m.key === activeMetric)!;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle>Performance Over Time</CardTitle>
          <div className="flex flex-wrap gap-1">
            {METRICS.map((m) => (
              <Button
                key={m.key}
                variant={activeMetric === m.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveMetric(m.key)}
                className="text-xs h-7"
              >
                {m.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 11 }} />
            <YAxis className="text-xs" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey={metric.key}
              stroke={metric.color}
              strokeWidth={2.5}
              dot={{ r: 3, fill: metric.color }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
